import moment from "moment";
import { HAKLAI_PRISA_ERRORS } from "./haklaiPrisaTexts";
import { getHaklaiPrisaPersonCreditPointsValue } from "./haklaiPrisaCreditPoints";

export const HAKLAI_PRISA_FIELD_KEYS = {
  personName: (personIndex) => `personName_${personIndex}`,
  birthOfClient: (personIndex) => `birthOfClient_${personIndex}`,
  dataPerYear: (personIndex) => `dataPerYear_${personIndex}`,
  income: (personIndex, yearIndex) => `income_${personIndex}_${yearIndex}`,
  zikui: (personIndex) => `zikui_${personIndex}`,
  personsData: "personsData",
  shouldTryPrisa: "shouldTryPrisa",
  isGilumConflict: "isGilumConflict"
};

const hasRawValue = (value) => value !== null && value !== undefined && String(value).trim() !== "";
const hasNumericValue = (value) => hasRawValue(value) && Number.isFinite(Number(value));
const PRISA_CREDIT_POINTS_MAX_VALUE = 100;

const buildAllPrisaErrors = (calc = {}) => {
  const errors = {};
  const mainCalc = calc?.mainCalc || {};
  const prisa = mainCalc?.prisa || { shouldTryPrisa: false, personsData: [] };
  if (!prisa.shouldTryPrisa) return errors;

  const today = moment();
  const soldDate = calc?.preCalc?.soldPropertyDate ? moment(calc.preCalc.soldPropertyDate) : null;
  const hundredYearsAgo = moment().add(-100, "years");

  const personsData = Array.isArray(prisa.personsData) ? prisa.personsData : [];
  if (personsData.length === 0) {
    errors[HAKLAI_PRISA_FIELD_KEYS.personsData] = HAKLAI_PRISA_ERRORS.personsRequired;
    return errors;
  }

  personsData.forEach((person, personIndex) => {
    if (!hasRawValue(person?.birthOfClient)) {
      errors[HAKLAI_PRISA_FIELD_KEYS.birthOfClient(personIndex)] = HAKLAI_PRISA_ERRORS.birthOfClientRequired;
    } else {
      const birthDate = moment(person.birthOfClient);
      if (!birthDate.isValid()) {
        errors[HAKLAI_PRISA_FIELD_KEYS.birthOfClient(personIndex)] = HAKLAI_PRISA_ERRORS.birthOfClientInvalid;
      } else if (birthDate.isAfter(today, "day")) {
        errors[HAKLAI_PRISA_FIELD_KEYS.birthOfClient(personIndex)] = HAKLAI_PRISA_ERRORS.birthOfClientFuture;
      } else if (soldDate && soldDate.isValid() && birthDate.isAfter(soldDate, "day")) {
        errors[HAKLAI_PRISA_FIELD_KEYS.birthOfClient(personIndex)] = HAKLAI_PRISA_ERRORS.birthOfClientAfterSale;
      } else if (birthDate.isBefore(hundredYearsAgo, "day")) {
        errors[HAKLAI_PRISA_FIELD_KEYS.birthOfClient(personIndex)] = HAKLAI_PRISA_ERRORS.birthOfClientTooOld;
      }
    }

    if (!Array.isArray(person?.dataPerYear) || person.dataPerYear.length === 0) {
      errors[HAKLAI_PRISA_FIELD_KEYS.dataPerYear(personIndex)] = HAKLAI_PRISA_ERRORS.yearsRequired;
      return;
    }

    const creditPoints = getHaklaiPrisaPersonCreditPointsValue(person);
    if (!hasNumericValue(creditPoints)) {
      errors[HAKLAI_PRISA_FIELD_KEYS.zikui(personIndex)] = HAKLAI_PRISA_ERRORS.creditPointsRequired;
    } else if (Number(creditPoints) > PRISA_CREDIT_POINTS_MAX_VALUE) {
      errors[HAKLAI_PRISA_FIELD_KEYS.zikui(personIndex)] = HAKLAI_PRISA_ERRORS.creditPointsInvalid;
    }
  });

  return errors;
};

export const getHaklaiPrisaFieldErrors = (calc = {}) => buildAllPrisaErrors(calc);

export const getHaklaiPrisaFieldErrorsForBlur = (calc = {}, touchedFields = {}) => {
  const allErrors = buildAllPrisaErrors(calc);
  const prisa = calc?.mainCalc?.prisa || { shouldTryPrisa: false, personsData: [] };

  const shouldReveal = (fieldName) => {
    if (touchedFields[fieldName]) return true;
    if (fieldName === HAKLAI_PRISA_FIELD_KEYS.shouldTryPrisa) return true;
    if (fieldName === HAKLAI_PRISA_FIELD_KEYS.personsData) return (prisa.personsData || []).length > 0;

    if (fieldName.startsWith("personName_")) {
      const personIndex = Number(fieldName.split("_")[1]);
      return hasRawValue(prisa.personsData?.[personIndex]?.personName);
    }
    if (fieldName.startsWith("birthOfClient_")) {
      const personIndex = Number(fieldName.split("_")[1]);
      return hasRawValue(prisa.personsData?.[personIndex]?.birthOfClient);
    }
    if (fieldName.startsWith("dataPerYear_")) {
      const personIndex = Number(fieldName.split("_")[1]);
      return Array.isArray(prisa.personsData?.[personIndex]?.dataPerYear);
    }
    if (fieldName.startsWith("income_")) {
      const [, personIndex, yearIndex] = fieldName.split("_").map(Number);
      return hasRawValue(prisa.personsData?.[personIndex]?.dataPerYear?.[yearIndex]?.income);
    }
    if (fieldName.startsWith("zikui_")) {
      const personIndex = Number(fieldName.split("_")[1]);
      return hasRawValue(getHaklaiPrisaPersonCreditPointsValue(prisa.personsData?.[personIndex]));
    }
    return false;
  };

  return Object.keys(allErrors).reduce((acc, fieldName) => {
    if (shouldReveal(fieldName)) {
      acc[fieldName] = allErrors[fieldName];
    }
    return acc;
  }, {});
};

export const getHaklaiPrisaSubmitError = (calc = {}) => {
  const errors = getHaklaiPrisaFieldErrors(calc);
  const firstErrorKey = Object.keys(errors)[0];
  return firstErrorKey ? errors[firstErrorKey] : null;
};
